/**
 * Admin-managed press asset slots.
 *
 * Four named slots back the public EPK. Once an admin uploads a file into a
 * slot the public /press page renders it automatically — no code change.
 * Client-safe module: definitions and types only, no server imports.
 */

export type PressSlotKind = "photo" | "logo";

export interface PressSlotDef {
  slot: string;
  label: string;
  kind: PressSlotKind;
  /** Journalist-facing caption rendered under the asset. */
  caption: string;
  /** Admin guidance. */
  recommended: string;
  accept: string;
}

export const PRESS_SLOTS: PressSlotDef[] = [
  {
    slot: "buddha-ka-press-primary",
    label: "Primary press portrait",
    kind: "photo",
    caption: "Buddha KA — primary approved press photograph",
    recommended: "Real photograph. JPG or PNG, portrait 4:5, at least 2000px on the long edge.",
    accept: "image/jpeg,image/png,image/webp,image/avif",
  },
  {
    slot: "buddha-ka-press-secondary",
    label: "Secondary press portrait",
    kind: "photo",
    caption: "Buddha KA — secondary approved press photograph",
    recommended: "Real photograph. JPG or PNG, portrait or landscape, at least 2000px on the long edge.",
    accept: "image/jpeg,image/png,image/webp,image/avif",
  },
  {
    slot: "kafrequency-primary-logo",
    label: "KAfrequency primary logo / wordmark",
    kind: "logo",
    caption: "KAfrequency — primary wordmark",
    recommended: "PNG with transparency, at least 1600px wide.",
    accept: "image/png,image/webp,image/avif",
  },
  {
    slot: "kafrequency-alternate-seal",
    label: "KAfrequency alternate seal",
    kind: "logo",
    caption: "KAfrequency — alternate seal",
    recommended: "PNG with transparency, square, at least 1200×1200.",
    accept: "image/png,image/webp,image/avif",
  },
];

export const PRESS_SLOT_IDS = PRESS_SLOTS.map((s) => s.slot);

export interface PressBrandAsset {
  slot: string;
  /** Browser-facing URL (signing proxy for storage-backed assets). */
  url: string;
  /** Durable stored reference (Supabase storage object URL or external URL). */
  sourceUrl?: string;
  alt: string;
  credit: string;
  updatedAt: string;
}

export type PressBrandAssetMap = Record<string, PressBrandAsset>;

export function slotDef(slot: string): PressSlotDef | undefined {
  return PRESS_SLOTS.find((s) => s.slot === slot);
}

/**
 * Parse a Supabase Storage object URL into { bucket, path }.
 *
 * The admin uploader stores a `/object/public/...` URL, but the `media`
 * bucket is private, so that URL is not retrievable by a browser. We keep the
 * stored value as the durable reference and derive a working URL at read time.
 */
export function parseStorageRef(url: string): { bucket: string; path: string } | null {
  const m = /\/storage\/v1\/object\/(?:public\/|sign\/|authenticated\/)?([^/]+)\/(.+?)(?:\?|$)/.exec(url);
  if (!m) return null;
  const bucket = m[1]!;
  const path = decodeURI(m[2]!);
  if (!bucket || !path) return null;
  return { bucket, path };
}

/** Stable, always-current public URL for a managed press asset slot. */
export function pressAssetProxyUrl(slot: string): string {
  return `/api/public/press-asset/${slot}`;
}
